import type { ComponentProps } from 'react';
import { action } from 'mobx';
import type { ChipFill } from '@sbol/nova';
import { QuestionModel } from '@/survey/logic/QuestionModel';
import { type Answer, type Header, HeaderType, MultiRestrictType } from '../../../types';

export class MatrixModel extends QuestionModel {
  rows: Header[];
  cols: Header[];
  isRatingScale10: boolean;

  constructor(...args: ConstructorParameters<typeof QuestionModel>) {
    super(...args);

    /**
     * Если включена рандомизация (question.is_random), то бэк перемешивает header_number, но не меняет позиции заголовков в списке.
     * И сортировка необходима.
     */
    this.rows = this.question.form_quest_headers
      .filter(({ header_type }) => header_type === HeaderType.ROW)
      .sort((a, b) => a.header_number - b.header_number);
    this.cols = this.question.form_quest_headers
      .filter(({ header_type }) => header_type === HeaderType.COL)
      .sort((a, b) => a.header_number - b.header_number);
    this.isRatingScale10 =
      this.rows.length === 1 &&
      this.cols.length === 10 &&
      this.cols.every((col) => String(col.header_number) === col.header_text.trim());
  }

  @action
  handleClickChip: ComponentProps<typeof ChipFill>['onClick'] = (e) => {
    if (!(e.currentTarget instanceof HTMLButtonElement)) {
      return;
    }

    if (!e.currentTarget.dataset.rowId || !e.currentTarget.dataset.colId) {
      return;
    }

    const selectedHeaderRowId = +e.currentTarget.dataset.rowId;
    const selectedHeaderColId = +e.currentTarget.dataset.colId;

    const answerToSelect = this.question.form_answers.find(
      (a) => a.header_col_id === selectedHeaderColId && a.header_row_id === selectedHeaderRowId
    );
    if (!answerToSelect) {
      return;
    }

    let answerCanBeSelected = true;

    const newSelectedAnswers = this.selectedAnswers.filter((a1) => {
      // Если ответ уже был выбран, то повторное нажатие его удаляет
      if (a1 === answerToSelect) {
        answerCanBeSelected = false;
        return false;
      }

      // Удаляем другие ответы в колонке или строке
      if (this.question.multi_restrict_type === MultiRestrictType.ONE_IN_COLUMN) {
        return !this.question.form_answers.some((a2) => a2 === a1 && a2.header_col_id === selectedHeaderColId);
      } else if (this.question.multi_restrict_type === MultiRestrictType.ONE_IN_ROW) {
        return !this.question.form_answers.some((a2) => a2 === a1 && a2.header_row_id === selectedHeaderRowId);
      }

      return true;
    });

    if (answerCanBeSelected) {
      newSelectedAnswers.push(answerToSelect);
    }

    this.set_selectedAnswers(newSelectedAnswers);
  };

  isAnswerSelected(rowId: Answer['header_row_id'], colId: Answer['header_col_id']): boolean {
    return this.selectedAnswers.some((a) => a.header_row_id === rowId && a.header_col_id === colId);
  }
}
